import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhoneIcon from '@mui/icons-material/Phone';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PaymentsIcon from '@mui/icons-material/Payments';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AdminSidebar from './AdminSidebar';

const AdminPortal = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load orders from Firebase
  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    const result = await getOrders();
    if (result.success) {
      // Filter orders to show only "processing" and "confirmed" status
      const filteredOrders = result.orders.filter(
        order => order.status === 'processing' || order.status === 'confirmed'
      );
      setOrders(filteredOrders);
    } else {
      setError('Failed to load orders: ' + result.error);
    }
    setLoading(false);
  };

  const handleManageAllOrders = () => {
    navigate('/admin-portal/orders');
  };

  const handleViewOrder = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Handle Firebase Timestamp
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const statusColors = {
    processing: 'bg-purple-100 text-purple-800',
    confirmed: 'bg-blue-100 text-blue-800'
  };

  const isWithin24h = (date) => {
    if (!date) return false;
    const dateObj = date.toDate && typeof date.toDate === 'function' ? date.toDate() : new Date(date);
    return (Date.now() - dateObj.getTime()) <= (24 * 60 * 60 * 1000);
  };

  const pendingCount = orders.filter((order) => order.status === 'processing').length;
  const processingTodayCount = orders.filter((order) => isWithin24h(order.dateTime || order.createdAt)).length;
  const revenue24h = orders
    .filter((order) => isWithin24h(order.dateTime || order.createdAt))
    .reduce((sum, order) => sum + Number(order.totalAmount || order.total || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="orders" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="flex justify-between items-center px-4 md:px-6 py-2 w-full sticky top-20 z-30 bg-white border-b-2 border-gray-200 h-[64px]">
            <div className="flex items-center">
              <button className="md:hidden mr-2 text-gray-500 hover:text-primary transition-colors" type="button">
                <MenuIcon />
              </button>
              <h1 className="text-xl font-bold text-primary">MMK Admin</h1>
            </div>
            <button
              onClick={handleManageAllOrders}
              className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              <AssignmentIcon className="!w-5 !h-5" />
              All Orders
            </button>
          </header>

          <main className="flex-grow p-4 md:p-6 max-w-7xl mx-auto w-full">
            <div className="mb-8 flex justify-between items-end border-b-2 border-gray-200 pb-2">
              <div>
                <h2 className="text-3xl md:text-4xl text-primary font-bold">Incoming Orders</h2>
                <p className="text-gray-500 mt-1">Manage and track precision hardware shipments.</p>
              </div>
              {!loading && (
                <span className="px-3 py-1 bg-primary text-white text-sm font-semibold rounded-full">
                  {orders.length}
                </span>
              )}
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-gray-200 p-4 rounded shadow-sm flex flex-col">
                <span className="text-xs uppercase tracking-wider text-gray-500 mb-2">Pending Review</span>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-primary">{pendingCount}</span>
                  <PendingActionsIcon className="!w-8 !h-8 text-blue-400" />
                </div>
              </div>
              <div className="bg-white border border-gray-200 p-4 rounded shadow-sm flex flex-col">
                <span className="text-xs uppercase tracking-wider text-gray-500 mb-2">Processing Today</span>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-primary">{processingTodayCount}</span>
                  <PrecisionManufacturingIcon className="!w-8 !h-8 text-indigo-400" />
                </div>
              </div>
              <div className="bg-white border border-gray-200 p-4 rounded shadow-sm flex flex-col">
                <span className="text-xs uppercase tracking-wider text-gray-500 mb-2">Total Revenue (24h)</span>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-primary">RM {formatNumber(revenue24h)}</span>
                  <PaymentsIcon className="!w-8 !h-8 text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded shadow-sm overflow-hidden">
              {loading && (
                <div className="p-8 text-center">
                  <div className="text-gray-500">Loading orders...</div>
                </div>
              )}
              {!loading && orders.length === 0 && (
                <div className="p-8 text-center">
                  <div className="text-gray-500">No orders with processing or confirmed status found.</div>
                </div>
              )}
              {!loading && orders.length > 0 && (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-primary/10 text-primary text-xs uppercase tracking-wider border-b border-gray-200">
                          <th className="p-3 w-12 text-center">
                            <input type="checkbox" className="rounded" />
                          </th>
                          <th className="p-3">Order ID</th>
                          <th className="p-3">Customer</th>
                          <th className="p-3">Phone</th>
                          <th className="p-3">Date</th>
                          <th className="p-3">Status</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="text-sm text-gray-700 divide-y divide-gray-200">
                        {orders.map((order) => (
                          <tr key={order.id} className="hover:bg-gray-50 transition-colors group cursor-pointer" onClick={() => handleViewOrder(order.id)}>
                            <td className="p-3 text-center">
                              <input type="checkbox" className="rounded" onClick={(e) => e.stopPropagation()} />
                            </td>
                            <td className="p-3 font-mono font-bold">{order.orderId || order.id?.slice(-8).toUpperCase()}</td>
                            <td className="p-3">
                              <div className="font-semibold text-primary">{order.purchaserName}</div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <PhoneIcon className="!w-4 !h-4 text-gray-400" />
                                {order.purchaserPhoneNumber}
                              </div>
                            </td>
                            <td className="p-3">{formatDate(order.dateTime || order.createdAt)}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                              </span>
                            </td>
                            <td className="p-3 text-right font-bold text-primary">RM {formatNumber(order.totalAmount || order.total || 0)}</td>
                            <td className="p-3 text-right">
                              <button
                                className="text-gray-500 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewOrder(order.id);
                                }}
                              >
                                <MoreVertIcon />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="border-t border-gray-200 p-3 flex items-center justify-between bg-white">
                    <span className="text-sm text-gray-500">
                      Showing 1 to {Math.min(orders.length, 10)} of {orders.length} entries
                    </span>
                    <div className="flex items-center space-x-1">
                      <button className="px-2 py-1 border border-gray-300 rounded text-gray-500" disabled>
                        &lt;
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center font-bold bg-primary text-white rounded">1</button>
                      <button className="px-2 py-1 border border-gray-300 rounded text-gray-500" disabled>
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPortal; 